/**
 * Shell onboarding: indietro sugli step fatti, avanti solo in sequenza (no skip).
 */
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Loader2, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { OnboardingPublicPayload } from "@/lib/onboarding-public";
import { cn } from "@/lib/utils";
import {
  CUSTOMER_STEPS,
  canOpenCustomerStep,
  customerStepHint,
  customerStepPath,
  getCustomerStepStates,
  isCustomerStepDone,
  nextCustomerStep,
  prevCustomerStep,
  type CustomerStepId,
} from "./onboardingSteps";

export interface OnboardingShellProps {
  token: string;
  step: CustomerStepId;
  tenantName?: string | null;
  payload?: OnboardingPublicPayload | null;
  loading?: boolean;
  error?: string | null;
  /** @deprecated sempre max-w-3xl per allineare tutti gli step */
  wide?: boolean;
  hideContinue?: boolean;
  continueLabel?: string;
  continueDisabled?: boolean;
  onContinue?: () => void;
  onBack?: () => void;
  backDisabled?: boolean;
  hideBack?: boolean;
  children: React.ReactNode;
}

export function OnboardingShell({
  token,
  step,
  tenantName,
  payload,
  loading,
  error,
  wide: _wide,
  hideContinue,
  continueLabel = "Continua",
  continueDisabled,
  onContinue,
  onBack,
  backDisabled,
  hideBack,
  children,
}: OnboardingShellProps) {
  const navigate = useNavigate();
  const states = getCustomerStepStates(step, payload);
  const prev = prevCustomerStep(step);
  const next = nextCustomerStep(step);
  const hint = customerStepHint(step, payload);
  const maxW = "max-w-3xl";
  const currentDone = isCustomerStepDone(step, payload);
  // Continua di default: solo se lo step corrente è già completato
  const canDefaultContinue = !!next && currentDone;

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }
    if (!prev) return;
    navigate(customerStepPath(token, prev));
  };

  const handleContinue = () => {
    if (onContinue) {
      onContinue();
      return;
    }
    if (!next || !canDefaultContinue) return;
    navigate(customerStepPath(token, next));
  };

  return (
    <div className="min-h-[100dvh] flex flex-col bg-gradient-to-b from-stone-100 to-stone-50 text-foreground">
      <header className="sticky top-0 z-20 border-b border-border/50 bg-white/80 backdrop-blur-sm">
        <div className={cn("mx-auto px-4", maxW)}>
          <div className="flex items-center justify-between py-3">
            <span className="text-sm font-semibold tracking-tight">Mindlead</span>
            <Link
              to="/construction/login"
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Login
            </Link>
          </div>

          {tenantName ? (
            <div className="pb-3 space-y-0.5">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                Attivazione
              </p>
              <h1 className="text-2xl font-semibold tracking-tight leading-tight">
                {tenantName}
              </h1>
            </div>
          ) : null}

          {/* Paginazione step — no skip avanti */}
          <nav className="pb-3 space-y-2" aria-label="Passi onboarding">
            <div className="grid grid-cols-4 gap-2">
              {CUSTOMER_STEPS.map((s, i) => {
                const st = states[s.id];
                const active = st === "current";
                const done = st === "done";
                const locked = st === "locked";
                const openable = canOpenCustomerStep(s.id, step, payload);
                return (
                  <button
                    key={s.id}
                    type="button"
                    disabled={!openable}
                    title={
                      locked
                        ? "Completa gli step precedenti"
                        : done
                          ? "Torna a questo step"
                          : undefined
                    }
                    onClick={() => {
                      if (!canOpenCustomerStep(s.id, step, payload)) return;
                      navigate(customerStepPath(token, s.id));
                    }}
                    className={cn(
                      "min-h-[3.25rem] rounded-xl border px-2 py-2 text-left transition-colors",
                      active
                        ? "border-emerald-600/40 bg-emerald-50 text-emerald-950"
                        : done
                          ? "border-border/70 bg-white text-foreground hover:border-border"
                          : locked
                            ? "border-border/40 bg-stone-100/80 text-muted-foreground/70 cursor-not-allowed"
                            : "border-border/70 bg-white text-muted-foreground",
                    )}
                  >
                    <p className="text-[10px] uppercase tracking-wide opacity-80 leading-none mb-1">
                      Step {i + 1}
                    </p>
                    <p className="text-xs font-medium leading-snug flex items-center gap-1 truncate">
                      {done && !active ? (
                        <Check className="h-3 w-3 text-emerald-600 shrink-0" strokeWidth={2.5} />
                      ) : locked ? (
                        <Lock className="h-3 w-3 shrink-0 opacity-60" />
                      ) : null}
                      <span className="truncate">{s.label}</span>
                    </p>
                  </button>
                );
              })}
            </div>
            <div className="flex gap-1.5">
              {CUSTOMER_STEPS.map((s) => {
                const st = states[s.id];
                const filled = st === "current" || st === "done";
                return (
                  <div
                    key={s.id}
                    className={cn(
                      "h-1 flex-1 rounded-full",
                      filled ? "bg-emerald-600" : "bg-stone-200",
                    )}
                  />
                );
              })}
            </div>
          </nav>
        </div>
      </header>

      <main
        className={cn(
          "flex-1 w-full mx-auto px-4 pt-8",
          "pb-[calc(5.25rem+env(safe-area-inset-bottom))]",
          maxW,
        )}
      >
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="rounded-2xl border border-border/60 bg-card p-6 text-center space-y-2">
            <p className="text-lg font-semibold">Link non disponibile</p>
            <p className="text-sm text-muted-foreground leading-relaxed">{error}</p>
          </div>
        ) : (
          <div key={step} className="space-y-5">
            {children}
          </div>
        )}
      </main>

      {!loading && !error ? (
        <div className="fixed bottom-0 inset-x-0 z-20 border-t border-border/50 bg-white/90 backdrop-blur-sm pb-[env(safe-area-inset-bottom)]">
          <div className={cn("mx-auto px-4 py-3 flex items-center gap-2", maxW)}>
            {!hideBack ? (
              <Button
                type="button"
                variant="outline"
                className="rounded-xl h-11 min-w-[7.5rem] gap-1.5 shrink-0"
                disabled={backDisabled || (!onBack && !prev)}
                onClick={handleBack}
              >
                <ArrowLeft className="h-4 w-4" />
                Indietro
              </Button>
            ) : (
              <div className="min-w-[7.5rem] shrink-0" />
            )}

            <p className="flex-1 text-center text-[11px] text-muted-foreground truncate px-1">
              {hint}
            </p>

            {!hideContinue ? (
              <Button
                type="button"
                className="rounded-xl h-11 min-w-[7.5rem] gap-1.5 shrink-0"
                disabled={
                  continueDisabled || (!onContinue && !canDefaultContinue)
                }
                onClick={handleContinue}
              >
                {continueLabel}
                <ArrowRight className="h-4 w-4" />
              </Button>
            ) : (
              <div className="min-w-[7.5rem] shrink-0" />
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
